// src/services/DashboardService.js

import { 
  collection, 
  doc, 
  getDocs, 
  onSnapshot, 
  query, 
  orderBy, 
  limit, 
  where,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Timestamp,
  FieldValue 
} from 'firebase/firestore';
import { db } from '../firebase-config';

class DashboardService {
  constructor() {
    this.unsubscribeCallbacks = new Map();
  }

  /**
   * Get dashboard metrics (counts) for a company
   */
  async getDashboardMetrics(companyId) {
    try {
      const [departmentsSnapshot, staffSnapshot, activeSessionsSnapshot] = await Promise.all([
        getDocs(collection(db, 'companies', companyId, 'departments')),
        getDocs(collection(db, 'companies', companyId, 'staff')),
        getDocs(collection(db, 'companies', companyId, 'activeSessions'))
      ]);

      // ARCHIVE: staff count must exclude archived employees.
      // Legacy docs with no `active` field are treated as active (default !== false).
      const activeStaffCount = staffSnapshot.docs
        .filter(d => d.data().active !== false).length;

      return {
        departments: departmentsSnapshot.size,
        staffMembers: activeStaffCount,
        activeSessions: activeSessionsSnapshot.size
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      return {
        departments: 0,
        staffMembers: 0,
        activeSessions: 0
      };
    }
  }

  /**
   * Get all departments for filtering
   */
  async getDepartments(companyId) {
    try {
      const departmentsSnapshot = await getDocs(collection(db, 'companies', companyId, 'departments'));
      const departments = [];
      
      departmentsSnapshot.forEach(doc => {
        departments.push({
          id: doc.id,
          name: doc.data().name
        });
      });
      
      return departments.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error fetching departments:', error);
      return [];
    }
  }

  /**
   * Get currently active staff with their session details
   */
  async getCurrentlyActiveStaff(companyId) {
    try {
      const activeSessionsRef = collection(db, 'companies', companyId, 'activeSessions');
      const staffRef = collection(db, 'companies', companyId, 'staff');
      const departmentsRef = collection(db, 'companies', companyId, 'departments');

      // Get active sessions
      const activeSessionsSnapshot = await getDocs(activeSessionsRef);
      
      if (activeSessionsSnapshot.empty) {
        return [];
      }

      // Get staff and departments data
      const [staffSnapshot, departmentsSnapshot] = await Promise.all([
        getDocs(staffRef),
        getDocs(departmentsRef)
      ]);

      // Create lookup maps
      const staffMap = new Map();
      staffSnapshot.forEach(doc => {
        staffMap.set(doc.id, { id: doc.id, ...doc.data() });
      });

      const departmentMap = new Map();
      departmentsSnapshot.forEach(doc => {
        departmentMap.set(doc.id, { id: doc.id, ...doc.data() });
      });

      // Combine active session data with staff and department info
      const activeStaff = [];
      activeSessionsSnapshot.forEach(doc => {
        const session = doc.data();
        const staff = staffMap.get(doc.id);
        const department = departmentMap.get(session.departmentId);

        if (staff && department) {
          const clockInTime = session.clockIn?.toDate ? session.clockIn.toDate() : new Date(session.clockIn);
          const currentTime = new Date();
          const sessionDuration = clockInTime ? 
            Math.floor((currentTime - clockInTime) / (1000 * 60)) : 0; // in minutes

          activeStaff.push({
            staffId: doc.id,
            staffName: staff.name,
            departmentId: session.departmentId,
            departmentName: department.name,
            clockIn: clockInTime,
            sessionDuration,
            onBreak: session.onBreak || false,
            breakStart: session.breakStart?.toDate ? session.breakStart.toDate() : null,
            deviceType: session.deviceType || 'Unknown',
            location: session.location,
            isLongSession: sessionDuration > 600 // Alert if >10 hours (600 minutes)
          });
        }
      });

      // Sort by clock-in time (most recent first)
      return activeStaff.sort((a, b) => b.clockIn - a.clockIn);

    } catch (error) {
      console.error('Error fetching currently active staff:', error);
      return [];
    }
  }

  /**
   * Get recent activity - FIREBASE STRUCTURE CORRECTED
   *
   * BUGFIX (Recent Activity tab stuck empty until hard refresh):
   * The outer catch previously swallowed ANY error from this function
   * (staff/department lookups, activeSessions lookup, or any of the
   * per-staff timeEntries sub-queries) and silently returned [] as if
   * there were simply no activity. This masked genuine transient
   * failures (e.g. an auth-token/permissions race right after mount)
   * as a legitimate "no activity" empty state, with no error surfaced
   * to DashboardContext/ActivityFeed and no way to recover without a
   * full page reload. The outer catch now re-throws so the caller
   * (DashboardContext.loadRecentActivity) can correctly set
   * activityError and DashboardContext's activeTab-triggered retry
   * effect can attempt to recover automatically.
   */
  async getRecentActivity(companyId, limitCount = 25) {
    try {
      const activities = [];
      
      // Get today's date string in local timezone
      const today = new Date();
      const todayString = today.getFullYear() + '-' + 
                         String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                         String(today.getDate()).padStart(2, '0');

      // Get staff and departments for lookup
      const [staffSnapshot, departmentsSnapshot] = await Promise.all([
        getDocs(collection(db, 'companies', companyId, 'staff')),
        getDocs(collection(db, 'companies', companyId, 'departments'))
      ]);

      const staffMap = new Map();
      staffSnapshot.forEach(doc => {
        staffMap.set(doc.id, { id: doc.id, name: doc.data().name });
      });

      const departmentMap = new Map();
      departmentsSnapshot.forEach(doc => {
        departmentMap.set(doc.id, { id: doc.id, name: doc.data().name });
      });

      // Get recent activities from active sessions (current clock-ins and breaks)
      const activeSessionsSnapshot = await getDocs(collection(db, 'companies', companyId, 'activeSessions'));
      
      activeSessionsSnapshot.forEach(doc => {
        const session = doc.data();
        const staff = staffMap.get(doc.id);
        const department = departmentMap.get(session.departmentId);

        if (staff && department && session.clockIn) {
          const clockInTime = session.clockIn.toDate();
          
          // Check if clock-in is from today using local timezone
          const clockInDateString = clockInTime.getFullYear() + '-' + 
                                   String(clockInTime.getMonth() + 1).padStart(2, '0') + '-' + 
                                   String(clockInTime.getDate()).padStart(2, '0');
          
          if (clockInDateString === todayString) {
            activities.push({
              id: `clockin-${doc.id}-${clockInTime.getTime()}`,
              staffId: doc.id,
              staffName: staff.name,
              departmentId: session.departmentId,
              departmentName: department.name,
              type: 'clock-in',
              timestamp: clockInTime,
              deviceType: session.deviceType || 'Unknown'
            });
          }

          // Add break activities if on break (and break started today)
          if (session.onBreak && session.breakStart) {
            const breakStartTime = session.breakStart.toDate();
            const breakDateString = breakStartTime.getFullYear() + '-' + 
                                   String(breakStartTime.getMonth() + 1).padStart(2, '0') + '-' + 
                                   String(breakStartTime.getDate()).padStart(2, '0');
            
            if (breakDateString === todayString) {
              activities.push({
                id: `break-start-${doc.id}-${breakStartTime.getTime()}`,
                staffId: doc.id,
                staffName: staff.name,
                departmentId: session.departmentId,
                departmentName: department.name,
                type: 'break-start',
                timestamp: breakStartTime,
                deviceType: session.deviceType || 'Unknown'
              });
            }
          }
        }
      });

      // Get recent activities from timeEntries - CORRECTED PATH STRUCTURE
      const timeEntriesPromises = [];
      
      staffMap.forEach((staff, staffId) => {
        // Correct path structure: companies/{companyId}/timeEntries/{staffId}/entries
        const timeEntriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
        const todayQuery = query(
          timeEntriesRef,
          where('date', '==', todayString),
          limit(5)
        );
        
        timeEntriesPromises.push(
          getDocs(todayQuery)
            .then(entriesSnapshot => {
              const staffActivities = [];
              
              entriesSnapshot.forEach(doc => {
                const entry = doc.data();
                const department = departmentMap.get(entry.departmentId);
                
                if (department) {
                  // Process clock-outs
                  if (entry.clockOuts && entry.clockOuts.length > 0) {
                    entry.clockOuts.forEach((clockOut, index) => {
                      const clockOutTime = clockOut.toDate();
                      staffActivities.push({
                        id: `clockout-${doc.id}-${index}-${clockOutTime.getTime()}`,
                        staffId: staffId,
                        staffName: staff.name,
                        departmentId: entry.departmentId,
                        departmentName: department.name,
                        type: 'clock-out',
                        timestamp: clockOutTime,
                        deviceType: entry.deviceType || 'Unknown',
                        totalWorkDuration: entry.totalWorkDuration
                      });
                    });
                  }

                  // Process break starts from completed sessions
                  if (entry.breakStarts && entry.breakStarts.length > 0) {
                    entry.breakStarts.forEach((breakStart, index) => {
                      const breakStartTime = breakStart.toDate();
                      staffActivities.push({
                        id: `break-start-completed-${doc.id}-${index}-${breakStartTime.getTime()}`,
                        staffId: staffId,
                        staffName: staff.name,
                        departmentId: entry.departmentId,
                        departmentName: department.name,
                        type: 'break-start',
                        timestamp: breakStartTime,
                        deviceType: entry.deviceType || 'Unknown'
                      });
                    });
                  }

                  // Process break ends
                  if (entry.breakEnds && entry.breakEnds.length > 0) {
                    entry.breakEnds.forEach((breakEnd, index) => {
                      const breakEndTime = breakEnd.toDate();
                      staffActivities.push({
                        id: `break-end-${doc.id}-${index}-${breakEndTime.getTime()}`,
                        staffId: staffId,
                        staffName: staff.name,
                        departmentId: entry.departmentId,
                        departmentName: department.name,
                        type: 'break-end',
                        timestamp: breakEndTime,
                        deviceType: entry.deviceType || 'Unknown'
                      });
                    });
                  }
                }
              });
              
              return staffActivities;
            })
            .catch(error => {
              console.log(`Skipping timeEntries for ${staff.name}:`, error.message);
              return [];
            })
        );
      });

      // Wait for all timeEntries queries to complete
      const timeEntriesResults = await Promise.all(timeEntriesPromises);
      
      // Flatten the results and add to activities
      timeEntriesResults.forEach(staffActivities => {
        activities.push(...staffActivities);
      });

      // Remove duplicates and sort by timestamp (most recent first)
      const uniqueActivities = activities.filter((activity, index, self) => 
        index === self.findIndex(a => a.id === activity.id)
      );

      uniqueActivities.sort((a, b) => b.timestamp - a.timestamp);
      return uniqueActivities.slice(0, limitCount);

    } catch (error) {
      console.error('Error fetching recent activity:', error);
      // BUGFIX: previously `return [];` here — silently masked real
      // failures as "no recent activity" with no way to recover short
      // of a full page reload. Re-throw so DashboardContext.loadRecentActivity
      // can set activityError and its activeTab-triggered retry effect
      // can attempt an automatic recovery.
      throw error;
    }
  }

  /**
   * Get dashboard alerts
   */
  async getDashboardAlerts(companyId) {
    try {
      const activeStaff = await this.getCurrentlyActiveStaff(companyId);
      const alerts = [];

      activeStaff.forEach(staff => {
        // Long session alert (>10 hours)
        if (staff.isLongSession) {
          alerts.push({
            id: `long-session-${staff.staffId}`,
            type: 'long-session',
            severity: 'warning',
            staffId: staff.staffId,
            staffName: staff.staffName,
            departmentName: staff.departmentName,
            message: `${staff.staffName} has been clocked in for ${Math.floor(staff.sessionDuration / 60)} hours`,
            timestamp: new Date()
          });
        }

        // Long break alert (>2 hours on break)
        if (staff.onBreak && staff.breakStart) {
          const breakDuration = Math.floor((new Date() - staff.breakStart) / (1000 * 60));
          if (breakDuration > 120) { // 2 hours
            alerts.push({
              id: `long-break-${staff.staffId}`,
              type: 'long-break',
              severity: 'info',
              staffId: staff.staffId,
              staffName: staff.staffName,
              departmentName: staff.departmentName,
              message: `${staff.staffName} has been on break for ${Math.floor(breakDuration / 60)} hours`,
              timestamp: new Date()
            });
          }
        }
      });

      return alerts.sort((a, b) => b.timestamp - a.timestamp);

    } catch (error) {
      console.error('Error fetching dashboard alerts:', error);
      return [];
    }
  }

  // =============================================================================
  // HOURS ACCOUNT OUTLIERS - NEW (Part 2, multi-location visibility)
  // Read-only: pulls the already-computed ytdBalance written by the nightly
  // Cloud Function (hours-account-nightly-recalc). No calculation happens
  // here, and no hoursAccounts documents are ever created by this method
  // (raw getDoc, never ensureAccount-style writes).
  // =============================================================================

  /**
   * Get the largest positive and negative Hours Account YTD balances across
   * every staff member in the company (spanning all departments/locations),
   * plus company-wide positive/balanced/negative counts for the dashboard
   * "Stundensaldo-Übersicht" summary bar.
   *
   * @param {string} companyId
   * @param {Object} options
   * @param {number} options.topCount - how many entries to return per list (default 5)
   * @returns {Promise<{
   *   topPositive: Array<{staffId, staffName, departmentName, ytdBalance}>,
   *   topNegative: Array<{staffId, staffName, departmentName, ytdBalance}>,
   *   staffWithoutAccount: number,
   *   counts: { positive: number, balanced: number, negative: number, total: number }
   * }>}
   */
  async getHoursBalanceOutliers(companyId, options = {}) {
    const { topCount = 5 } = options;
    const currentYear = new Date().getFullYear().toString();

    try {
      const [staffSnapshot, departmentsSnapshot] = await Promise.all([
        getDocs(collection(db, 'companies', companyId, 'staff')),
        getDocs(collection(db, 'companies', companyId, 'departments'))
      ]);

      if (staffSnapshot.empty) {
        return {
          topPositive: [],
          topNegative: [],
          staffWithoutAccount: 0,
          counts: { positive: 0, balanced: 0, negative: 0, total: 0 }
        };
      }

      const departmentMap = new Map();
      departmentsSnapshot.forEach(d => {
        departmentMap.set(d.id, d.data().name || 'Unknown Department');
      });

      // ARCHIVE: exclude archived staff from outlier calculations.
      // Legacy docs with no `active` field are treated as active (default !== false).
      const staffList = staffSnapshot.docs
        .filter(d => d.data().active !== false)
        .map(d => ({ id: d.id, ...d.data() }));

      const balances = await Promise.all(
        staffList.map(async (staff) => {
          const primaryDeptId =
            staff.primaryDepartmentId ||
            (staff.departmentIds && staff.departmentIds[0]) ||
            null;

          const departmentName = primaryDeptId
            ? (departmentMap.get(primaryDeptId) || 'Unknown Department')
            : 'Unassigned';

          try {
            const accountRef = doc(
              db, 'companies', companyId,
              'staff', staff.id,
              'hoursAccounts', currentYear
            );
            const accountDoc = await getDoc(accountRef);

            if (!accountDoc.exists()) {
              return {
                staffId: staff.id,
                staffName: staff.name || 'Unknown',
                departmentName,
                ytdBalance: null
              };
            }

            const data = accountDoc.data();

            return {
              staffId: staff.id,
              staffName: staff.name || 'Unknown',
              departmentName,
              ytdBalance: typeof data.ytdBalance === 'number' ? data.ytdBalance : null
            };
          } catch (err) {
            console.error(
              `DashboardService: Error reading hours account for staff ${staff.id}:`, err
            );
            return {
              staffId: staff.id,
              staffName: staff.name || 'Unknown',
              departmentName,
              ytdBalance: null
            };
          }
        })
      );

      const withBalance = balances.filter(b => b.ytdBalance !== null);
      const staffWithoutAccount = balances.length - withBalance.length;

      const topPositive = [...withBalance]
        .filter(b => b.ytdBalance > 0)
        .sort((a, b) => b.ytdBalance - a.ytdBalance)
        .slice(0, topCount);

      const topNegative = [...withBalance]
        .filter(b => b.ytdBalance < 0)
        .sort((a, b) => a.ytdBalance - b.ytdBalance) // most negative first
        .slice(0, topCount);

      // Company-wide balance distribution for the "Stundensaldo-Übersicht" bar.
      // Threshold of +/-0.5h treats near-zero balances as "Ausgeglichen" (balanced).
      const positive = withBalance.filter(b => b.ytdBalance > 0.5).length;
      const negative = withBalance.filter(b => b.ytdBalance < -0.5).length;
      const balanced = withBalance.length - positive - negative;

      return {
        topPositive,
        topNegative,
        staffWithoutAccount,
        counts: { positive, balanced, negative, total: balances.length }
      };

    } catch (error) {
      console.error('DashboardService: Error fetching hours balance outliers:', error);
      return {
        topPositive: [],
        topNegative: [],
        staffWithoutAccount: 0,
        counts: { positive: 0, balanced: 0, negative: 0, total: 0 }
      };
    }
  }

  // =============================================================================
  // ADMIN TIME MANAGEMENT METHODS - NEW
  // =============================================================================

  /**
   * Get specific staff's active session - ADMIN METHOD
   */
  async getStaffActiveSession(companyId, staffId) {
    try {
      console.log('DashboardService: Getting active session for staff:', staffId);
      
      const activeSessionRef = doc(db, 'companies', companyId, 'activeSessions', staffId);
      const sessionDoc = await getDoc(activeSessionRef);
      
      if (!sessionDoc.exists()) {
        return null;
      }
      
      const sessionData = sessionDoc.data();
      
      // Get staff and department names
      const [staffDoc, departmentDoc] = await Promise.all([
        getDoc(doc(db, 'companies', companyId, 'staff', staffId)),
        getDoc(doc(db, 'companies', companyId, 'departments', sessionData.departmentId))
      ]);
      
      const staffName = staffDoc.exists() ? staffDoc.data().name : 'Unknown Staff';
      const departmentName = departmentDoc.exists() ? departmentDoc.data().name : 'Unknown Department';
      
      const clockInTime = sessionData.clockIn?.toDate();
      const breakStartTime = sessionData.breakStart?.toDate();
      const currentTime = new Date();
      const sessionDuration = clockInTime ? 
        Math.floor((currentTime - clockInTime) / (1000 * 60)) : 0;
      
      return {
        staffId,
        staffName,
        departmentId: sessionData.departmentId,
        departmentName,
        clockIn: clockInTime,
        sessionDuration,
        onBreak: sessionData.onBreak || false,
        breakStart: breakStartTime,
        deviceType: sessionData.deviceType || 'Unknown',
        location: sessionData.location,
        isLongSession: sessionDuration > 600
      };
      
    } catch (error) {
      console.error('DashboardService: Error getting staff active session:', error);
      throw new Error(`Failed to get active session: ${error.message}`);
    }
  }

  /**
   * Admin clock out staff with time adjustments - ADMIN METHOD
   */
  async adminClockOutStaff(companyId, staffId, adjustments = {}) {
    try {
      console.log('DashboardService: Admin clocking out staff:', staffId, 'with adjustments:', adjustments);
      
      const batch = writeBatch(db);
      
      // 1. Get current active session
      const activeSessionRef = doc(db, 'companies', companyId, 'activeSessions', staffId);
      const sessionDoc = await getDoc(activeSessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error('No active session found for this staff member');
      }
      
      const sessionData = sessionDoc.data();
      const originalClockIn = sessionData.clockIn;
      const departmentId = sessionData.departmentId;
      const onBreak = sessionData.onBreak || false;
      const breakStart = sessionData.breakStart;
      
      // 2. Apply adjustments or use current time
      const clockOutTime = adjustments.clockOutTime ? 
        Timestamp.fromDate(new Date(adjustments.clockOutTime)) : 
        Timestamp.now();
      
      const adjustedClockIn = adjustments.clockInTime ? 
        Timestamp.fromDate(new Date(adjustments.clockInTime)) : 
        originalClockIn;
      
      // 3. Calculate session durations
      const durations = this.calculateSessionDurations(
        [adjustedClockIn],
        [clockOutTime],
        breakStart ? [breakStart] : [],
        onBreak ? [] : (breakStart ? [adjustments.breakEndTime ? 
          Timestamp.fromDate(new Date(adjustments.breakEndTime)) : clockOutTime] : [])
      );
      
      // 4. Get or create time entry for today
      const dateString = clockOutTime.toDate().toISOString().split('T')[0];
      const timeEntriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
      const todayQuery = query(timeEntriesRef, where('date', '==', dateString));
      const existingEntries = await getDocs(todayQuery);
      
      let timeEntryRef;
      let isNewEntry = false;
      
      if (existingEntries.empty) {
        // Create new time entry
        timeEntryRef = doc(timeEntriesRef);
        isNewEntry = true;
      } else {
        // Use existing entry
        timeEntryRef = existingEntries.docs[0].ref;
      }
      
      // 5. Prepare time entry data
      const timeEntryData = {
        departmentId: departmentId,
        date: dateString,
        sessionStatus: 'Clocked Out',
        deviceType: sessionData.deviceType || 'Web Admin',
        deviceId: 'admin-web-app',
        note: adjustments.note || 'Clocked out by admin'
      };
      
      if (isNewEntry) {
        // New entry - set all fields
        timeEntryData.clockIns = [adjustedClockIn];
        timeEntryData.clockOuts = [clockOutTime];
        timeEntryData.breakStarts = breakStart ? 
          (adjustments.breakStartTime ? [Timestamp.fromDate(new Date(adjustments.breakStartTime))] : [breakStart]) : [];
        timeEntryData.breakEnds = (breakStart && !onBreak) ? 
          (adjustments.breakEndTime ? [Timestamp.fromDate(new Date(adjustments.breakEndTime))] : [clockOutTime]) : [];
        timeEntryData.totalWorkDuration = durations.workDuration;
        timeEntryData.totalBreakDuration = durations.breakDuration;
      } else {
        // Existing entry - append to arrays and accumulate durations
        const existingData = existingEntries.docs[0].data();
        timeEntryData.clockIns = [...(existingData.clockIns || []), adjustedClockIn];
        timeEntryData.clockOuts = [...(existingData.clockOuts || []), clockOutTime];
        timeEntryData.breakStarts = breakStart ? 
          [...(existingData.breakStarts || []), (adjustments.breakStartTime ? 
            Timestamp.fromDate(new Date(adjustments.breakStartTime)) : breakStart)] : 
          (existingData.breakStarts || []);
        timeEntryData.breakEnds = (breakStart && !onBreak) ? 
          [...(existingData.breakEnds || []), (adjustments.breakEndTime ? 
            Timestamp.fromDate(new Date(adjustments.breakEndTime)) : clockOutTime)] : 
          (existingData.breakEnds || []);
        timeEntryData.totalWorkDuration = (existingData.totalWorkDuration || 0) + durations.workDuration;
        timeEntryData.totalBreakDuration = (existingData.totalBreakDuration || 0) + durations.breakDuration;
      }
      
      // 6. Add to batch
      if (isNewEntry) {
        batch.set(timeEntryRef, timeEntryData);
      } else {
        batch.update(timeEntryRef, timeEntryData);
      }
      
      // 7. Delete active session
      batch.delete(activeSessionRef);
      
      // 8. Commit batch
      await batch.commit();
      
      console.log('DashboardService: Admin clock out completed successfully');
      
      return {
        success: true,
        timeEntryId: timeEntryRef.id,
        workDuration: durations.workDuration,
        breakDuration: durations.breakDuration,
        clockOutTime: clockOutTime.toDate()
      };
      
    } catch (error) {
      console.error('DashboardService: Error in admin clock out:', error);
      throw new Error(`Failed to clock out staff: ${error.message}`);
    }
  }

  /**
   * Get staff time entries with pagination - ADMIN METHOD
   */
  async getStaffTimeEntries(companyId, staffId, options = {}) {
    try {
      console.log('DashboardService: Getting time entries for staff:', staffId);
      
      const {
        startDate = null,
        endDate = null,
        limit: limitCount = 50,
        lastDoc = null
      } = options;
      
      const timeEntriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
      let timeEntriesQuery = query(timeEntriesRef, orderBy('date', 'desc'));
      
      // Add date range filters
      if (startDate) {
        const startDateString = startDate.toISOString().split('T')[0];
        timeEntriesQuery = query(timeEntriesQuery, where('date', '>=', startDateString));
      }
      
      if (endDate) {
        const endDateString = endDate.toISOString().split('T')[0];
        timeEntriesQuery = query(timeEntriesQuery, where('date', '<=', endDateString));
      }
      
      // Add pagination
      if (limitCount) {
        timeEntriesQuery = query(timeEntriesQuery, limit(limitCount));
      }
      
      const snapshot = await getDocs(timeEntriesQuery);
      
      if (snapshot.empty) {
        return {
          entries: [],
          hasMore: false,
          lastDoc: null
        };
      }
      
      // Get staff and department info for context
      const [staffDoc, departmentsSnapshot] = await Promise.all([
        getDoc(doc(db, 'companies', companyId, 'staff', staffId)),
        getDocs(collection(db, 'companies', companyId, 'departments'))
      ]);
      
      const staffName = staffDoc.exists() ? staffDoc.data().name : 'Unknown Staff';
      
      const departmentMap = new Map();
      departmentsSnapshot.forEach(doc => {
        departmentMap.set(doc.id, doc.data().name);
      });
      
      // Transform entries
      const entries = snapshot.docs.map(doc => {
        const data = doc.data();
        const departmentName = departmentMap.get(data.departmentId) || 'Unknown Department';
        
        return {
          id: doc.id,
          staffId,
          staffName,
          departmentId: data.departmentId,
          departmentName,
          date: data.date,
          clockIns: (data.clockIns || []).map(ts => ts.toDate()),
          clockOuts: (data.clockOuts || []).map(ts => ts.toDate()),
          breakStarts: (data.breakStarts || []).map(ts => ts.toDate()),
          breakEnds: (data.breakEnds || []).map(ts => ts.toDate()),
          totalWorkDuration: data.totalWorkDuration || 0,
          totalBreakDuration: data.totalBreakDuration || 0,
          sessionStatus: data.sessionStatus || 'Unknown',
          deviceType: data.deviceType || 'Unknown',
          deviceId: data.deviceId || '',
          note: data.note || '',
          // Calculate additional metrics
          sessionsCount: (data.clockIns || []).length,
          isComplete: (data.clockIns || []).length === (data.clockOuts || []).length,
          lastModified: doc._document?.version?.timestamp?.toDate() || new Date()
        };
      });
      
      return {
        entries,
        hasMore: snapshot.docs.length === limitCount,
        lastDoc: snapshot.docs[snapshot.docs.length - 1]
      };
      
    } catch (error) {
      console.error('DashboardService: Error getting staff time entries:', error);
      throw new Error(`Failed to get time entries: ${error.message}`);
    }
  }

  /**
   * Update existing time entry with adjustments - ADMIN METHOD
   */
  async updateTimeEntry(companyId, staffId, entryId, adjustments) {
    try {
      console.log('DashboardService: Updating time entry:', entryId, 'with adjustments:', adjustments);
      
      const timeEntryRef = doc(db, 'companies', companyId, 'timeEntries', staffId, 'entries', entryId);
      
      // Get current entry
      const entryDoc = await getDoc(timeEntryRef);
      if (!entryDoc.exists()) {
        throw new Error('Time entry not found');
      }
      
      const currentData = entryDoc.data();
      
      // Apply adjustments to timestamp arrays
      const updatedData = {
        clockIns: adjustments.clockIns ? 
          adjustments.clockIns.map(time => Timestamp.fromDate(new Date(time))) : 
          currentData.clockIns || [],
        clockOuts: adjustments.clockOuts ? 
          adjustments.clockOuts.map(time => Timestamp.fromDate(new Date(time))) : 
          currentData.clockOuts || [],
        breakStarts: adjustments.breakStarts ? 
          adjustments.breakStarts.map(time => Timestamp.fromDate(new Date(time))) : 
          currentData.breakStarts || [],
        breakEnds: adjustments.breakEnds ? 
          adjustments.breakEnds.map(time => Timestamp.fromDate(new Date(time))) : 
          currentData.breakEnds || []
      };
      
      // Validate timestamp arrays
      this.validateTimeEntry(updatedData);
      
      // Recalculate durations
      const durations = this.calculateSessionDurations(
        updatedData.clockIns,
        updatedData.clockOuts,
        updatedData.breakStarts,
        updatedData.breakEnds
      );
      
      // Update additional fields if provided
      if (adjustments.note !== undefined) {
        updatedData.note = adjustments.note;
      }
      
      if (adjustments.sessionStatus) {
        updatedData.sessionStatus = adjustments.sessionStatus;
      }
      
      // Set calculated durations
      updatedData.totalWorkDuration = durations.workDuration;
      updatedData.totalBreakDuration = durations.breakDuration;
      
      // Add admin modification marker
      updatedData.lastModifiedBy = 'admin';
      updatedData.lastModifiedAt = Timestamp.now();
      
      await updateDoc(timeEntryRef, updatedData);
      
      console.log('DashboardService: Time entry updated successfully');
      
      return {
        success: true,
        workDuration: durations.workDuration,
        breakDuration: durations.breakDuration,
        updatedAt: new Date()
      };
      
    } catch (error) {
      console.error('DashboardService: Error updating time entry:', error);
      throw new Error(`Failed to update time entry: ${error.message}`);
    }
  }

  /**
   * Calculate session durations - implements iOS business logic
   */
  calculateSessionDurations(clockIns, clockOuts, breakStarts, breakEnds) {
    try {
      let totalWorkDuration = 0;
      let totalBreakDuration = 0;
      
      // Convert all timestamps to dates for calculation
      const clockInDates = clockIns.map(ts => ts.toDate ? ts.toDate() : new Date(ts));
      const clockOutDates = clockOuts.map(ts => ts.toDate ? ts.toDate() : new Date(ts));
      const breakStartDates = breakStarts.map(ts => ts.toDate ? ts.toDate() : new Date(ts));
      const breakEndDates = breakEnds.map(ts => ts.toDate ? ts.toDate() : new Date(ts));
      
      // Calculate work duration for each session (clock-in to clock-out pair)
      for (let i = 0; i < clockInDates.length; i++) {
        const clockInTime = clockInDates[i];
        const clockOutTime = clockOutDates[i] || new Date(); // Use current time if not clocked out
        
        if (clockOutTime <= clockInTime) {
          console.warn('DashboardService: Clock out time is before clock in time, skipping session');
          continue;
        }
        
        // Calculate total session duration
        const sessionDuration = (clockOutTime - clockInTime) / (1000 * 60); // in minutes
        
        // Calculate break duration for this session
        let sessionBreakDuration = 0;
        
        // Find breaks that occurred during this session
        for (let j = 0; j < breakStartDates.length; j++) {
          const breakStartTime = breakStartDates[j];
          const breakEndTime = breakEndDates[j];
          
          // Only count breaks that started after this clock-in and before this clock-out
          if (breakStartTime >= clockInTime && breakStartTime <= clockOutTime) {
            const effectiveBreakEnd = breakEndTime || clockOutTime; // If still on break, use clock out time
            
            // Make sure break end is within the session
            const actualBreakEnd = effectiveBreakEnd > clockOutTime ? clockOutTime : effectiveBreakEnd;
            
            const breakDuration = (actualBreakEnd - breakStartTime) / (1000 * 60); // in minutes
            sessionBreakDuration += Math.max(0, breakDuration);
          }
        }
        
        // Work duration = total session time - break time
        const sessionWorkDuration = Math.max(0, sessionDuration - sessionBreakDuration);
        
        totalWorkDuration += sessionWorkDuration;
        totalBreakDuration += sessionBreakDuration;
        
        console.log(`Session ${i + 1}: Total=${sessionDuration.toFixed(1)}min, Work=${sessionWorkDuration.toFixed(1)}min, Break=${sessionBreakDuration.toFixed(1)}min`);
      }
      
      return {
        workDuration: Math.round(totalWorkDuration), // Round to nearest minute
        breakDuration: Math.round(totalBreakDuration)
      };
      
    } catch (error) {
      console.error('DashboardService: Error calculating durations:', error);
      return {
        workDuration: 0,
        breakDuration: 0
      };
    }
  }

  /**
   * Validate time entry data - ADMIN METHOD
   */
  validateTimeEntry(timeEntryData) {
    const { clockIns, clockOuts, breakStarts, breakEnds } = timeEntryData;
    
    // Basic validation
    if (!clockIns || clockIns.length === 0) {
      throw new Error('At least one clock-in time is required');
    }
    
    if (clockOuts.length > clockIns.length) {
      throw new Error('Cannot have more clock-outs than clock-ins');
    }
    
    if (breakStarts.length < breakEnds.length) {
      throw new Error('Cannot have more break ends than break starts');
    }
    
    // Validate chronological order within each array
    const validateChronological = (timestamps, name) => {
      for (let i = 1; i < timestamps.length; i++) {
        const prev = timestamps[i - 1].toDate ? timestamps[i - 1].toDate() : new Date(timestamps[i - 1]);
        const curr = timestamps[i].toDate ? timestamps[i].toDate() : new Date(timestamps[i]);
        if (curr <= prev) {
          throw new Error(`${name} must be in chronological order`);
        }
      }
    };
    
    validateChronological(clockIns, 'Clock-in times');
    validateChronological(clockOuts, 'Clock-out times');
    validateChronological(breakStarts, 'Break start times');
    validateChronological(breakEnds, 'Break end times');
    
    // Validate that clock-outs are after corresponding clock-ins
    for (let i = 0; i < clockOuts.length; i++) {
      const clockIn = clockIns[i].toDate ? clockIns[i].toDate() : new Date(clockIns[i]);
      const clockOut = clockOuts[i].toDate ? clockOuts[i].toDate() : new Date(clockOuts[i]);
      
      if (clockOut <= clockIn) {
        throw new Error(`Clock-out ${i + 1} must be after corresponding clock-in`);
      }
    }
    
    // Validate that break ends are after corresponding break starts
    for (let i = 0; i < breakEnds.length; i++) {
      const breakStart = breakStarts[i].toDate ? breakStarts[i].toDate() : new Date(breakStarts[i]);
      const breakEnd = breakEnds[i].toDate ? breakEnds[i].toDate() : new Date(breakEnds[i]);
      
      if (breakEnd <= breakStart) {
        throw new Error(`Break end ${i + 1} must be after corresponding break start`);
      }
    }
    
    console.log('DashboardService: Time entry validation passed');
    return true;
  }

  // =============================================================================
  // REAL-TIME SUBSCRIPTION METHODS
  // =============================================================================

  /**
   * Set up real-time listener for company settings - SYNC WITH iOS
   */
  subscribeToCompanySettings(companyId, callback) {
    const companyRef = doc(db, 'companies', companyId);
    
    const unsubscribe = onSnapshot(companyRef, 
      (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            console.log('DashboardService: Company settings updated from Firestore:', data);
            callback(data);
          } else {
            console.warn('DashboardService: Company document does not exist');
            callback(null);
          }
        } catch (error) {
          console.error('DashboardService: Error in company settings subscription:', error);
          callback(null);
        }
      },
      (error) => {
        console.error('DashboardService: Error subscribing to company settings:', error);
        callback(null);
      }
    );

    this.unsubscribeCallbacks.set(`companySettings-${companyId}`, unsubscribe);
    return unsubscribe;
  }

  /**
   * Set up real-time listener for active sessions
   */
  subscribeToActiveStaff(companyId, callback) {
    const activeSessionsRef = collection(db, 'companies', companyId, 'activeSessions');
    
    const unsubscribe = onSnapshot(activeSessionsRef, 
      async (snapshot) => {
        try {
          const activeStaff = await this.getCurrentlyActiveStaff(companyId);
          callback(activeStaff);
        } catch (error) {
          console.error('Error in active staff subscription:', error);
          callback([]);
        }
      },
      (error) => {
        console.error('Error subscribing to active sessions:', error);
        callback([]);
      }
    );

    this.unsubscribeCallbacks.set(`activeStaff-${companyId}`, unsubscribe);
    return unsubscribe;
  }

  /**
   * Set up real-time listener for recent activity - ENHANCED VERSION
   *
   * NOTE: getRecentActivity() now re-throws on failure (see bugfix comment
   * above) instead of silently resolving to []. This inner try/catch still
   * guards against that so the onSnapshot listener itself never dies from
   * an uncaught rejection — it just reports [] to this specific callback
   * invocation, exactly as before. The important part of the fix lives in
   * DashboardContext.loadRecentActivity, which now receives the real error
   * (via the one-shot call path) instead of a masked empty array.
   */
  subscribeToRecentActivity(companyId, callback) {
    const unsubscribes = [];

    // Listen to active sessions for real-time clock-ins and breaks
    const activeSessionsRef = collection(db, 'companies', companyId, 'activeSessions');
    
    const activeSessionsUnsub = onSnapshot(activeSessionsRef, 
      async () => {
        try {
          const recentActivity = await this.getRecentActivity(companyId, 25);
          callback(recentActivity);
        } catch (error) {
          console.error('Error in recent activity subscription:', error);
          callback([]);
        }
      },
      (error) => {
        console.error('Error subscribing to recent activity:', error);
        callback([]);
      }
    );
    
    unsubscribes.push(activeSessionsUnsub);

    // Combined unsubscribe function
    const combinedUnsubscribe = () => {
      unsubscribes.forEach(unsub => unsub());
    };

    this.unsubscribeCallbacks.set(`recentActivity-${companyId}`, combinedUnsubscribe);
    return combinedUnsubscribe;
  }

  /**
   * Set up real-time listener for dashboard metrics
   */
  subscribeToDashboardMetrics(companyId, callback) {
    const unsubscribes = [];

    // Listen to departments
    const departmentsRef = collection(db, 'companies', companyId, 'departments');
    unsubscribes.push(onSnapshot(departmentsRef, () => {
      this.getDashboardMetrics(companyId).then(callback).catch(error => {
        console.error('Error in metrics subscription:', error);
        callback({ departments: 0, staffMembers: 0, activeSessions: 0 });
      });
    }));

    // Listen to staff
    const staffRef = collection(db, 'companies', companyId, 'staff');
    unsubscribes.push(onSnapshot(staffRef, () => {
      this.getDashboardMetrics(companyId).then(callback).catch(error => {
        console.error('Error in metrics subscription:', error);
        callback({ departments: 0, staffMembers: 0, activeSessions: 0 });
      });
    }));

    // Listen to active sessions
    const activeSessionsRef = collection(db, 'companies', companyId, 'activeSessions');
    unsubscribes.push(onSnapshot(activeSessionsRef, () => {
      this.getDashboardMetrics(companyId).then(callback).catch(error => {
        console.error('Error in metrics subscription:', error);
        callback({ departments: 0, staffMembers: 0, activeSessions: 0 });
      });
    }));

    const combinedUnsubscribe = () => {
      unsubscribes.forEach(unsub => unsub());
    };

    this.unsubscribeCallbacks.set(`dashboardMetrics-${companyId}`, combinedUnsubscribe);
    return combinedUnsubscribe;
  }

  /**
   * Cleanup all subscriptions
   */
  unsubscribeAll() {
    this.unsubscribeCallbacks.forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing:', error);
      }
    });
    this.unsubscribeCallbacks.clear();
  }

  /**
   * Format duration helper
   */
  formatDuration(minutes) {
    if (!minutes || minutes < 0) return '0m';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }

  /**
   * Format time helper
   */
  formatTime(date) {
    if (!date) return '';
    
    try {
      return date.toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return '';
    }
  }

  /**
   * Get relative time helper
   */
  getRelativeTime(timestamp) {
    try {
      const now = new Date();
      const diff = Math.floor((now - timestamp) / 1000); // in seconds

      if (diff < 60) {
        return 'Just now';
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60);
        return `${minutes}m ago`;
      } else if (diff < 86400) {
        const hours = Math.floor(diff / 3600);
        return `${hours}h ago`;
      } else if (diff < 604800) {
        const days = Math.floor(diff / 86400);
        return `${days}d ago`;
      } else {
        return timestamp.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        });
      }
    } catch (error) {
      console.error('Error getting relative time:', error);
      return '';
    }
  }
}

export default new DashboardService();